import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Home, LogOut, List, PlusSquare, Users, Calendar, MessageCircle, HelpCircle, Shield, Grid, Users as UsersGroup, Menu, X, Image, Layers } from 'react-feather';
import darkIconP from '../../assets/dark-iconp.png';
import logoBlack from '../../assets/LogoBlackCropped.png';
import AuthService from '../../services/authService';
import PointsPurchaseModal from './PointsPurchaseModal';
import SupportChat from '../common/SupportChat';
import '../admin/AdminDashboard.css';
import './AgentDashboard.css';
import './AgentPWA.css';

const AgentLayout = ({
  children,
  activeTab,
  agentInfo,
  onDashboard,
  onMyListings,
  onAddProperty,
  onLogout,
  onInvitations,
  onShowings,
  onConversations,
  onAvailableLeads,
  onTeamRoster,
  onMediaLibrary,
}) => {
  const [isTeamLead, setIsTeamLead] = useState(false);
  const [teamMemberCount, setTeamMemberCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [agentPoints, setAgentPoints] = useState(0);
  const [agentStatus, setAgentStatus] = useState(null); // 'inactive' => cancelled account, drives the cancellation banner
  const [pauseEndsAt, setPauseEndsAt] = useState(null); // when set + status 'paused', drives the pause banner
  const [showPointsModal, setShowPointsModal] = useState(false);
  const [showSupportChat, setShowSupportChat] = useState(false);
  const [showConversationsModal, setShowConversationsModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // PWA standalone-mode detection. True when the page is running as an
  // installed PWA (launched from home screen, no browser chrome). When true,
  // the sidebar collapses into a hamburger drawer. In a regular browser tab,
  // this stays false and the sidebar renders exactly as it does today.
  const [isPWA, setIsPWA] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(display-mode: standalone)');
    const update = () => setIsPWA(mq.matches || window.navigator.standalone === true);
    update();
    // Listen for changes (e.g. user installs PWA mid-session). Older Safari
    // uses addListener instead of addEventListener — handle both.
    if (mq.addEventListener) {
      mq.addEventListener('change', update);
      return () => mq.removeEventListener('change', update);
    } else if (mq.addListener) {
      mq.addListener(update);
      return () => mq.removeListener(update);
    }
  }, []);

  // Add an `is-pwa` class to <body> when in PWA mode, so styles can reach
  // content that's rendered outside the dashboard tree (e.g. modals via
  // createPortal). The class on .dashboard-container only reaches descendants;
  // the body class reaches everything.
  useEffect(() => {
    if (isPWA) {
      document.body.classList.add('is-pwa');
      return () => document.body.classList.remove('is-pwa');
    }
  }, [isPWA]);

  // Lock body scroll when drawer is open (prevents background scrolling
  // behind the overlay on mobile)
  useEffect(() => {
    if (mobileMenuOpen) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [mobileMenuOpen]);

  // Properties expanded by default, Users expanded by default, Content expanded by default
  const [expandedGroups, setExpandedGroups] = useState({
    properties: true,
    users: true,
    content: true,
  });
  const toggleGroup = (key) =>
    setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  // Team lead status
  useEffect(() => {
    const checkTeamLeadStatus = async () => {
      try {
        const response = await fetch(
          'https://back-end-x7tx.onrender.com/api/agents/me/is-team-lead',
          { headers: { Authorization: `Bearer ${AuthService.getToken()}` } }
        );
        const data = await response.json();
        if (data.success) {
          setIsTeamLead(data.isTeamLead);
          setTeamMemberCount(data.teamMemberCount || 0);
        }
      } catch (error) {
        console.error('Error checking team lead status:', error);
      }
    };
    checkTeamLeadStatus();
  }, []);

  // Unread message count polling
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await fetch(
          'https://back-end-x7tx.onrender.com/api/messages/unread-count',
          { headers: { Authorization: `Bearer ${AuthService.getToken()}` } }
        );
        const data = await response.json();
        if (data.success) {
          setUnreadCount(data.data.unreadCount);
        }
      } catch (error) {
        console.error('Error fetching unread count:', error);
      }
    };
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Agent points balance — fetched on mount + after points modal closes.
  // Used by the PWA top header to display the agent's current point total.
  useEffect(() => {
    const fetchAgentPoints = async () => {
      try {
        const response = await fetch(
          'https://back-end-x7tx.onrender.com/api/users/profile',
          { headers: { Authorization: `Bearer ${AuthService.getToken()}` } }
        );
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setAgentPoints(data.data.pointsBalance || 0);
            setAgentStatus(data.data.agentStatus || null);
            setPauseEndsAt(data.data.pauseEndsAt || null);
          }
        }
      } catch (error) {
        console.error('Error fetching agent points:', error);
      }
    };
    fetchAgentPoints();
    // Refetch when the points modal closes (in case agent just topped up)
    if (!showPointsModal) {
      fetchAgentPoints();
    }
  }, [showPointsModal]);

  // Navigation structure — mirrors admin pattern (standalone Dashboard + grouped sections + standalone extras)
  const dashboardItem = {
    id: 'dashboard',
    icon: <Grid size={16} />,
    label: 'Dashboard',
    onClick: onDashboard,
  };

  const navGroups = [
    {
      key: 'properties',
      label: 'Properties',
      icon: <Home size={16} />,
      items: [
        {
          id: 'listings',
          icon: <List size={16} />,
          label: 'Active Listings',
          onClick: onMyListings,
        },
        {
          id: 'add',
          icon: <PlusSquare size={16} />,
          label: 'Add Manual',
          onClick: onAddProperty,
        },
        {
          id: 'showings',
          icon: <Calendar size={16} />,
          label: 'Scheduled Showings',
          onClick: onShowings,
        },
      ],
    },
    {
      key: 'users',
      label: 'Users',
      icon: <UsersGroup size={16} />,
      items: [
        {
          id: 'invitations',
          icon: <Users size={16} />,
          label: 'User Management',
          onClick: onInvitations,
        },
        {
          id: 'available-leads',
          icon: <img src={darkIconP} alt="" style={{ width: 16, height: 16, objectFit: 'contain', display: 'block' }} />,
          label: 'Available Users',
          onClick: onAvailableLeads,
        },
      ],
    },
    {
      // Content section — generated assets across all listings (videos
      // today; social posts coming next). Sits at the bottom of the
      // grouped sections since it's a workspace people visit after
      // they've created listings + onboarded users, not a primary
      // top-of-funnel action.
      key: 'content',
      label: 'Content',
      icon: <Layers size={16} />,
      items: [
        {
          id: 'media',
          icon: <Image size={16} />,
          label: 'Media Library',
          onClick: onMediaLibrary,
        },
      ],
    },
  ];

  // Standalone items below the groups
  const standaloneItems = [
    {
      id: 'conversations',
      icon: <MessageCircle size={16} />,
      label: 'Conversations',
      // In PWA mode, intercept and show a modal directing agents to use the
      // main Lauft app for messaging. In desktop browser, behave normally.
      onClick: isPWA ? () => setShowConversationsModal(true) : onConversations,
      badge: unreadCount > 0 ? (unreadCount > 99 ? '99+' : unreadCount) : null,
    },
    // Help Center is hidden in PWA mode — the support button in the top
    // header replaces it (and opens the in-app chat instead of /docs)
    ...(!isPWA ? [{
      id: 'help',
      icon: <HelpCircle size={16} />,
      label: 'Help Center',
      onClick: () => {
        window.location.href = '/docs';
      },
    }] : []),
  ];

  if (isTeamLead) {
    standaloneItems.push({
      id: 'team-roster',
      icon: <Shield size={16} />,
      label: `Team Roster${teamMemberCount > 0 ? ` (${teamMemberCount})` : ''}`,
      onClick: onTeamRoster,
    });
  }

  const handleNavClick = (handler) => {
    if (typeof handler === 'function') {
      handler();
    }
    setMobileMenuOpen(false);
  };

  const handleLogout = (e) => {
    e.preventDefault();
    if (typeof onLogout === 'function') onLogout();
  };

  // ===== Cancelled-account banner actions =====
  // Both open a pre-filled email to team@lauft.app (same channel for now),
  // mirroring the dashboard lockout. Different subject so the team can triage.
  const buildAccountEmailLines = () => {
    const cu = AuthService.getCurrentUser() || {};
    return (
      `Agent Name: ${agentInfo?.name || cu.name || 'N/A'}\n` +
      `Agent ID: ${agentInfo?._id || cu._id || 'N/A'}\n` +
      `Email: ${agentInfo?.email || cu.email || 'N/A'}\n` +
      `Phone: ${agentInfo?.phone || cu.phone || 'N/A'}`
    );
  };

  const handleReactivateAccount = () => {
    const subject = encodeURIComponent('Reactivate Agent Account Request');
    const body = encodeURIComponent(
      `Hello,\n\nI would like to reactivate my Lauft agent account.\n\n` +
      `Account Details:\n${buildAccountEmailLines()}\n\n` +
      `Please let me know what's needed to restore my account.\n\nThank you.`
    );
    window.location.href = `mailto:team@lauft.app?subject=${subject}&body=${body}`;
  };

  const handleCancelledSupport = () => {
    const subject = encodeURIComponent('Support Request - Cancelled Account');
    const body = encodeURIComponent(
      `Hello,\n\nI need help with my cancelled Lauft agent account.\n\n` +
      `Account Details:\n${buildAccountEmailLines()}\n\n` +
      `My question:\n[Describe your issue here]\n\nThank you.`
    );
    window.location.href = `mailto:team@lauft.app?subject=${subject}&body=${body}`;
  };

  return (
    <div className={`dashboard-container ${isPWA ? 'is-pwa' : ''}`}>
      {/* Floating cancellation banner — fixed to the top of the viewport and
          portaled to <body> so it sits ABOVE everything, sidebar included,
          regardless of layout stacking contexts. Shows on every page while the
          account status is 'inactive' (cancelled). z-index is below the
          dashboard lockout overlay so that hard gate still covers it. */}
      {(agentStatus || agentInfo?.agentStatus) === 'inactive' && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 2147483000,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px 12px',
            padding: '13px 24px',
            background: '#fdeceb',
            borderBottom: '1px solid #f3c3c0',
            boxShadow: '0 4px 18px rgba(155, 44, 44, 0.18)',
          }}
        >
          <span style={{ fontSize: '0.88rem', fontWeight: 600, color: '#9b2c2c' }}>
            Your account has been canceled.
          </span>
          <span style={{ fontSize: '0.88rem', color: '#9b2c2c' }}>
            <span
              role="button"
              tabIndex={0}
              onClick={handleReactivateAccount}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleReactivateAccount(); }}
              style={{ fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: '2px', cursor: 'pointer' }}
            >
              Reactivate account
            </span>
            {' or '}
            <span
              role="button"
              tabIndex={0}
              onClick={handleCancelledSupport}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleCancelledSupport(); }}
              style={{ fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: '2px', cursor: 'pointer' }}
            >
              get support
            </span>
            .
          </span>
        </div>,
        document.body
      )}
      {/* Floating pause banner — same floating/portaled treatment as the
          cancellation banner, but informational amber (a pause isn't an error).
          Shows on every page while the account status is 'paused'. Reminds the
          agent what still works and that billing/renewals are frozen until the
          pause end date. Mutually exclusive with the cancel banner above. */}
      {(agentStatus || agentInfo?.agentStatus) === 'paused' && (pauseEndsAt || agentInfo?.pauseEndsAt) && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 2147483000,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px 12px',
            padding: '13px 24px',
            background: '#fef6e7',
            borderBottom: '1px solid #f5d98b',
            boxShadow: '0 4px 18px rgba(180, 130, 20, 0.18)',
          }}
        >
          <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#92600a' }}>
            Your account is on pause until{' '}
            {new Date(pauseEndsAt || agentInfo?.pauseEndsAt).toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}.
          </span>
          <span style={{ fontSize: '0.88rem', color: '#92600a' }}>
            You can still claim users, message users, and use points — but no billing or point renewals will occur until then.
          </span>
        </div>,
        document.body
      )}
      {/* PWA top header — only renders inside the installed PWA.
          Contains: hamburger (left), Lauft logo (center), Points + Support (right).
          Hidden in regular browser tabs via the .pwa-top-header CSS scope. */}
      {isPWA && (
        <header className="pwa-top-header">
          <button
            type="button"
            className="pwa-header-btn pwa-hamburger"
            aria-label="Open menu"
            onClick={() => setMobileMenuOpen(true)}
          >
            <Menu size={22} />
            {unreadCount > 0 && (
              <span className="pwa-hamburger-dot" aria-hidden="true" />
            )}
          </button>

          <div className="pwa-header-logo-wrap">
            <img
              src={logoBlack}
              alt="Lauft"
              className="pwa-header-logo"
            />
          </div>

          <div className="pwa-header-actions">
            <button
              type="button"
              className="pwa-header-points"
              onClick={() => setShowPointsModal(true)}
              aria-label={`${agentPoints} points — tap to add points`}
            >
              <span className="pwa-header-points-num">{agentPoints} Points</span>
              <span className="pwa-header-points-label">Add Points</span>
            </button>
            <button
              type="button"
              className="pwa-header-btn"
              onClick={() => setShowSupportChat(true)}
              aria-label="Get support"
            >
              <HelpCircle size={20} />
            </button>
          </div>
        </header>
      )}

      {/* Dim overlay behind the drawer. Tap to close. Always rendered in
          PWA mode so we get a fade-out animation on close. */}
      {isPWA && (
        <div
          className={`pwa-drawer-overlay ${mobileMenuOpen ? 'visible' : ''}`}
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`dashboard-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}
      >
        {/* Close button — only visible inside the PWA when drawer is open */}
        {isPWA && (
          <button
            type="button"
            className="pwa-drawer-close"
            aria-label="Close menu"
            onClick={() => setMobileMenuOpen(false)}
          >
            <X size={20} />
          </button>
        )}

        {/* Logo */}
        <div className="logo-container">
          <img
            src={require('../../assets/icon portal.png')}
            alt="Lauft"
            className="logo-image"
            style={{ borderRadius: '6px' }}
          />
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          <ul>
            {/* Dashboard — standalone top-level */}
            <li
              className={`nav-item ${activeTab === dashboardItem.id ? 'active' : ''}`}
              onClick={() => handleNavClick(dashboardItem.onClick)}
            >
              <span className="icon">{dashboardItem.icon}</span>
              <span>{dashboardItem.label}</span>
            </li>

            {/* Grouped sections */}
            {navGroups.map((group) => {
              const isExpanded = expandedGroups[group.key];
              const hasActiveChild = group.items.some((i) => i.id === activeTab);
              return (
                <React.Fragment key={group.key}>
                  <li
                    className={`nav-group-header ${hasActiveChild ? 'has-active' : ''}`}
                    onClick={() => toggleGroup(group.key)}
                  >
                    <span className="icon">{group.icon}</span>
                    <span className="group-label">{group.label}</span>
                    <span className={`chevron ${isExpanded ? 'open' : ''}`}>
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        width="14"
                        height="14"
                      >
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </span>
                  </li>
                  {isExpanded &&
                    group.items.map((item) => (
                      <li
                        key={item.id}
                        className={`nav-item nav-sub-item ${activeTab === item.id ? 'active' : ''}`}
                        onClick={() => handleNavClick(item.onClick)}
                      >
                        <span className="icon">{item.icon}</span>
                        <span>{item.label}</span>
                      </li>
                    ))}
                </React.Fragment>
              );
            })}

            {/* Standalone items below groups */}
            {standaloneItems.map((item) => (
              <li
                key={item.id}
                className={`nav-item ${activeTab === item.id ? 'active' : ''}`}
                onClick={() => handleNavClick(item.onClick)}
                style={{ position: 'relative', overflow: 'visible' }}
              >
                <span className="icon" style={{ position: 'relative', overflow: 'visible' }}>
                  {item.icon}
                  {item.badge && (
                    <span
                      className="agent-unread-dot"
                      style={{
                        position: 'absolute',
                        top: '-6px',
                        right: '-8px',
                        backgroundColor: '#dc2626',
                        color: '#ffffff',
                        fontSize: '0.6rem',
                        fontWeight: 700,
                        minWidth: '16px',
                        height: '16px',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0 4px',
                        lineHeight: 1,
                        border: '2px solid #ffffff',
                        zIndex: 2,
                        pointerEvents: 'none',
                      }}
                    >
                      {item.badge}
                    </span>
                  )}
                </span>
                <span>{item.label}</span>
              </li>
            ))}
          </ul>
        </nav>

        {/* Footer — brand mark + version + agent name + logout (matches admin) */}
        <div className="sidebar-footer">
          <div className="sidebar-brand">
            <img
              src="/lauft-logo.png"
              alt="Lauft"
              className="sidebar-brand-logo"
            />
            <div className="sidebar-brand-meta">
              <div className="sidebar-brand-version">Agent Portal v3.0.1</div>
              <div className="sidebar-brand-date">
                {agentInfo?.name || AuthService.getCurrentUser()?.name || 'Agent'}
              </div>
            </div>
          </div>
          <button className="sidebar-logout" onClick={handleLogout} type="button">
            <span className="icon">
              <LogOut size={16} />
            </span>
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content + Footer column */}
      <main className="dashboard-main" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <div style={{ flex: 1 }}>{children}</div>
        <footer style={{
          marginTop: '32px',
          padding: '22px 28px',
          backgroundColor: '#ffffff',
          border: '1px solid #e5e7eb',
          borderRadius: '14px',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '24px',
          position: 'relative',
          zIndex: 0
        }}>
          <div style={{ flex: '1 1 420px', minWidth: 0 }}>
            <p style={{
              margin: 0,
              fontSize: '0.82rem',
              color: '#4b5563',
              lineHeight: '1.5',
              maxWidth: '620px'
            }}>
              Lauft is redefining real estate by connecting buyers and agents through personality-driven matching and intuitive property discovery.
            </p>
            <div style={{
              marginTop: '12px',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '18px',
              fontSize: '0.76rem',
              color: '#6b7280'
            }}>
              <a
                href="https://lauft.app/terms"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#4b5563', textDecoration: 'none', fontWeight: 500, transition: 'color 0.12s' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#111'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#4b5563'}
              >
                Terms of Service
              </a>
              <a
                href="https://lauft.app/privacy"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#4b5563', textDecoration: 'none', fontWeight: 500, transition: 'color 0.12s' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#111'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#4b5563'}
              >
                Privacy Policy
              </a>
              <a
                href="mailto:team@lauft.app"
                style={{ color: '#4b5563', textDecoration: 'none', fontWeight: 500, transition: 'color 0.12s' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#111'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#4b5563'}
              >
                Contact Support
              </a>
              <span style={{ color: '#9ca3af', fontSize: '0.74rem' }}>
                © {new Date().getFullYear()} Lauft. All rights reserved.
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flexShrink: 0, alignItems: 'flex-end' }}>
            <a
              href="https://apps.apple.com/us/app/lauft/id6748095080"
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-flex', transition: 'opacity 0.12s' }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.85'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              <img
                src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                alt="Download on the App Store"
                style={{ width: '110px', height: 'auto', display: 'block' }}
              />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.lauft.app"
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-flex', transition: 'opacity 0.12s' }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.85'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                alt="Get it on Google Play"
                style={{ width: '110px', height: 'auto', display: 'block' }}
              />
            </a>
          </div>
        </footer>
      </main>

      {/* Points Purchase Modal — rendered at layout level so it's accessible
          from the PWA top header on every page */}
      {showPointsModal && (
        <PointsPurchaseModal
          onClose={() => setShowPointsModal(false)}
        />
      )}

      {/* Support Chat — opened by the Get Support button in the PWA top header */}
      <SupportChat
        isOpen={showSupportChat}
        onClose={() => setShowSupportChat(false)}
      />

      {/* Conversations modal — only shown in PWA mode when agent taps
          Conversations in the sidebar. Directs them to message users via
          the main Lauft app since messaging UX is better there. */}
      {showConversationsModal && (
        <div
          className="pwa-conv-modal-overlay"
          onClick={() => setShowConversationsModal(false)}
        >
          <div
            className="pwa-conv-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="pwa-conv-modal-close"
              onClick={() => setShowConversationsModal(false)}
              aria-label="Close"
            >
              <X size={20} />
            </button>

            <div className="pwa-conv-modal-icon">
              <img
                src={require('../../assets/icon portal.png')}
                alt="Lauft"
              />
            </div>

            <h3 className="pwa-conv-modal-title">Conversations</h3>

            <p className="pwa-conv-modal-body">
              Message your users directly in the primary Lauft App or from a computer in the Partner Portal.
            </p>

            <button
              type="button"
              className="pwa-conv-modal-cta"
              onClick={() => {
                window.location.href = 'lauft://conversations';
              }}
            >
              Open Lauft App
            </button>

            <button
              type="button"
              className="pwa-conv-modal-secondary"
              onClick={() => setShowConversationsModal(false)}
            >
              Not now
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentLayout;